import { fetchEventSource } from '@microsoft/fetch-event-source';
import type { AgentSummary } from "../models/AgentSummary";

export type SummaryRating = 'Up' | 'Down';

/** Where a failure occurred, recorded as the `phase` dimension on AISummaryClientError. */
export type ClientErrorPhase = 'request' | 'response' | 'stream' | 'rating' | 'feedback';

export interface ISummaryService {
    generateSummary(query: string, domain: string): Promise<AgentSummary>;
    /**
     * Streams an AI summary for the given query via Server-Sent Events.
     * Fires `onChunk` for each answer text fragment as it arrives,
     * `onDone` with the complete structured summary and the id to rate it by once the stream
     * ends, and `onError` if the request fails or the stream is interrupted.
     */
    generateSummaryStream(
        query: string,
        domain: string,
        onChunk: (text: string) => void,
        onDone: (summary: AgentSummary, summaryId: string | null) => void,
        onError: (message: string) => void,
        signal?: AbortSignal
    ): Promise<void>;

    /**
     * Records a thumbs up or thumbs down against the summary identified by `summaryId`.
     * The caller passes the id belonging to the summary on screen rather than the service
     * tracking a "current" one, so a rating cannot be attributed to a later summary.
     */
    submitRating(summaryId: string | null, rating: SummaryRating, domain: string): Promise<void>;

    /** Records written feedback against the summary identified by `summaryId`. */
    submitFeedback(summaryId: string | null, feedback: string, domain: string): Promise<void>;

    /**
     * Reports a failure the API cannot observe for itself — a request that never arrived,
     * a stream interrupted in transit, or a response the browser could not use.
     */
    reportClientError(phase: ClientErrorPhase, message: string, domain: string): Promise<void>;
}

export class SummaryService implements ISummaryService {
    /**
     * The id for the request currently in flight. Used only to attribute failures reported from
     * inside this service; ratings and feedback take an explicit id from the caller instead.
     */
    private inFlightSummaryId: string | null = null;

    async generateSummary(query: string, domain: string): Promise<AgentSummary> {
        const url = new URL('/search/generate-summary', domain).toString();
        this.inFlightSummaryId = null;

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ query })
            });

            if (response.ok) {
                const summary = await response.json() as AgentSummary;
                this.inFlightSummaryId = summary.summaryId ?? null;
                return summary;
            }

            // The status alone — the API already logged the failure it generated, and the
            // response body can carry server exception detail that should not leave the server.
            void this.reportClientError('response', `HTTP ${response.status}`, domain);

            return this.failureResult('Failed to generate summary.');

        } catch (err) {
            void this.reportClientError('request', errorMessage(err), domain);

            return this.failureResult('Error connecting to summary service.');
        }
    }

    /**
     * Dummy generateSummary method for local development / testing.
     *
     * Usage:
     * - Uncomment this method to simulate API responses without calling the backend.
     * - Returns a valid AgentSummary object with placeholder text and sources.
     * - Adds a short delay to mimic network latency (~300ms).
     */
    //async generateSummary(
    //    query: string,
    //    domain: string
    //): Promise<AgentSummary> {
    //    await new Promise(resolve => setTimeout(resolve, 300));

    //    return {
    //        answer: `Lorem ipsum dolor sit amet consectetur adipiscing elit. Quisque faucibus ex sapien vitae pellentesque sem placerat. In id cursus mi pretium tellus duis convallis. Tempus leo eu aenean sed diam urna tempor. Pulvinar vivamus fringilla lacus nec metus bibendum egestas. Iaculis massa nisl malesuada lacinia integer nunc posuere. Ut hendrerit semper vel class aptent taciti sociosqu. Ad litora torquent per conubia nostra inceptos himenaeos.`,
    //        confidence: 0.87,
    //        sources: [
    //            {
    //                title: "Source page 1",
    //                url: `https://www.facs.org/`,
    //                excerpt: "Lorem ipsum dolor sit amet, consectetur."
    //            },
    //            {
    //                title: "Source page 2",
    //                url: `https://www.facs.org/`,
    //                excerpt: "Lorem ipsum dolor sit amet, consectetur."
    //            },
    //            {
    //                title: "Source page 3",
    //                url: `https://www.facs.org/`,
    //                excerpt: "Lorem ipsum dolor sit amet, consectetur."
    //            }
    //        ]
    //    };
    //}

    /**
     * Streams an AI summary for the given query via Server-Sent Events.
     * Fires `onChunk` for each answer text fragment as it arrives,
     * `onDone` with the complete structured summary and the id to rate it by once the stream
     * ends, and `onError` if the request fails or the stream is interrupted.
     */
    async generateSummaryStream(
        query: string,
        domain: string,
        onChunk: (text: string) => void,
        onDone: (summary: AgentSummary, summaryId: string | null) => void,
        onError: (message: string) => void,
        signal?: AbortSignal
    ): Promise<void> {
        const url = new URL('/search/generate-summary/stream', domain).toString();
        this.inFlightSummaryId = null;
        let errorHandled = false;

        try {
            await fetchEventSource(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ query }),
                signal,
                onopen: async (response) => {
                    if (!response.ok) {
                        errorHandled = true;
                        // Status only. Statuses rejected before the handler runs — a rate limit,
                        // a gateway error — are invisible to the API's own telemetry, so they are
                        // worth reporting; the response body is not, since it can carry server
                        // exception detail.
                        void this.reportClientError('response', `HTTP ${response.status}`, domain);
                        onError('Failed to start stream.');
                        throw new Error(`HTTP ${response.status}`);
                    }
                },
                onmessage: (event) => {
                    // The API puts the summary id in the SSE id field on the first chunk and on the
                    // terminal event. fetch-event-source does not carry an id forward between
                    // messages, so hold on to the last one seen rather than reading it per event.
                    if (event.id) this.inFlightSummaryId = event.id;

                    if (event.event === 'done') {
                        onDone(JSON.parse(event.data) as AgentSummary, this.inFlightSummaryId);
                    } else if (event.event === 'error') {
                        errorHandled = true;
                        onError(event.data);
                    } else {
                        try {
                            const { text } = JSON.parse(event.data) as { text: string };
                            onChunk(text);
                        } catch {
                            // Malformed chunk — skip rather than abort the stream
                        }
                    }
                },
                onerror: (err) => {
                    throw err; // prevent auto-retry
                }
            });
        } catch (err) {
            if ((err as Error)?.name === 'AbortError' || errorHandled) return;
            // A stream that dies in transit is the one failure the API genuinely cannot see:
            // OperationCanceledException only fires when it is mid-write, and a request that never
            // arrived leaves no trace at all.
            void this.reportClientError('stream', errorMessage(err), domain);
            onError('Error connecting to summary service.');
        }
    }

    // ------------------------------
    // Feedback and failure reporting
    // ------------------------------
    // These post to the API rather than calling trackEvent directly, so the events are recorded
    // server-side where a tracker blocker cannot drop them. All three are fire and forget: a
    // telemetry failure must never surface in the UI.

    async submitRating(summaryId: string | null, rating: SummaryRating, domain: string): Promise<void> {
        // No id means the summary on screen was never correlated — an older API, or a stream that
        // ended without one. Report it rather than dropping the rating in silence, so lost
        // feedback stays countable.
        if (!summaryId) {
            void this.reportClientError('rating', `Rating '${rating}' discarded: no summary id.`, domain);
            return;
        }

        await this.postTelemetry('/search/summary-rating', { summaryId, rating }, domain);
    }

    async submitFeedback(summaryId: string | null, feedback: string, domain: string): Promise<void> {
        if (!feedback) return;

        if (!summaryId) {
            void this.reportClientError('feedback', 'Feedback discarded: no summary id.', domain);
            return;
        }

        await this.postTelemetry('/search/summary-feedback', { summaryId, feedback }, domain);
    }

    async reportClientError(phase: ClientErrorPhase, message: string, domain: string): Promise<void> {
        await this.postTelemetry('/search/summary-client-error', {
            summaryId: this.inFlightSummaryId,
            phase,
            message
        }, domain);
    }

    /**
     * Posts a telemetry payload. Never throws and never surfaces in the UI, but a rejected or
     * unreachable endpoint is warned about — otherwise a wrong route or a changed contract looks
     * exactly like success, which is the failure mode moving these events server-side was meant
     * to remove.
     *
     * `keepalive` lets the request outlive the page, since ratings are typically clicked moments
     * before the user navigates away.
     */
    private async postTelemetry(path: string, body: unknown, domain: string): Promise<void> {
        try {
            const response = await fetch(new URL(path, domain).toString(), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
                keepalive: true
            });

            if (!response.ok) {
                console.warn(`[facs-ai-summary] ${path} rejected the request: HTTP ${response.status}`);
            }
        } catch (err) {
            // Deliberately only logged, never reported back to the API — a failing beacon
            // reporting its own failure would loop.
            console.warn(`[facs-ai-summary] ${path} was unreachable:`, err);
        }
    }

    private failureResult(message: string): AgentSummary {
        return {
            answer: message,
            confidence: null,
            sources: []
        } as AgentSummary;
    }
}

function errorMessage(err: unknown): string {
    if (err instanceof Error) return `${err.name}: ${err.message}`;
    return String(err);
}
